/**
 * FHVitals SDK Service — v5.9.3
 * Wrapper para el SDK de FaceHeart que mide signos vitales usando la cámara
 * NOTA: El SDK se exporta como window.FHVitals (no FHVitalsSDK)
 *
 * Cambios v5.9.3 (vs 5.9.1-f):
 *  - Mayor sensibilidad para Cardiovascular BMI y CV Age.
 *  - Mejora del SpO2 Signal Quality Index.
 *  - Nuevos campos en `result`: hrv_indices (HF/LF/RMSSD/SDNN/SD1/SD2/etc.),
 *    image_quality, HbA1c, hemoglobin, cholesterol, afib, wrong_input_group_risk,
 *    health indices (activity/equilibrium/health/metabolism/relaxation/sleep),
 *    shock_index, reach_max_sbp/dbp, average_process_time.
 *  - Nuevas APIs: getCalibratedBP(ref_sbp, ref_dbp), getMiscalibrationRisk().
 */

// Dominios autorizados por FaceHeart
const AUTHORIZED_DOMAINS = [
  'we360-stg.saluddigital360.com',
  'we360.saluddigital360.com',
  'localhost',
  '127.0.0.1',
  'we360-dev.web.app',
  'we360-dev.firebaseapp.com',
  'dev.saluddigital360.com',
  'we360-486203.web.app',
  'we360-486203.firebaseapp.com'
];

// Auth URL del servidor FaceHeart
const AUTH_URL = "wss://hcs-va.faceheart.com:9443/wasm/v2";

// Path a los assets del SDK.
// CRÍTICO: el SDK carga `fhvitals_worker.js` como Web Worker CLÁSICO, que DEBE
// ser del MISMO ORIGEN que la página (los Workers cross-origin lanzan
// SecurityError). Por eso usamos `window.location.origin` (el dominio de la
// página) y NO REACT_APP_BACKEND_URL, que tras el deploy apunta a otro dominio.
// Al usar `${window.location.origin}/fhvitals`, Firebase Hosting sirve los assets
// estáticos directamente, garantizando same-origin y evitando llamadas al backend.
const ASSETS_PATH = `${window.location.origin}/fhvitals`;

/**
 * Verifica si el dominio actual está autorizado para usar el SDK
 */
export function isAuthorizedDomain() {
  const hostname = window.location.hostname;
  return AUTHORIZED_DOMAINS.includes(hostname);
}

/**
 * Verifica si el navegador soporta las tecnologías necesarias
 */
export function isSDKSupported() {
  // Verificar WebAssembly
  if (typeof WebAssembly !== 'object') {
    return { supported: false, reason: 'WebAssembly no soportado' };
  }
  
  // Verificar MediaDevices API (cámara)
  if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
    return { supported: false, reason: 'Acceso a cámara no disponible' };
  }
  
  // Verificar HTTPS (requerido para WebSocket Secure)
  if (window.location.protocol !== 'https:' && window.location.hostname !== 'localhost') {
    return { supported: false, reason: 'HTTPS requerido' };
  }
  
  return { supported: true };
}

/**
 * Clase para manejar el SDK de FHVitals
 */
export class FHVitalsService {
  constructor() {
    this.sdk = null;
    this.initialized = false;
    this.measuring = false;
    this.onResultCallback = null;
    this.onEventCallback = null;
  }

  /**
   * Inicializa el SDK
   * @param {Object} callbacks - { onResult, onEvent }
   * @returns {Promise<{error: number}>}
   */
  async init(callbacks) {
    console.log('[FHVitals] 🚀 Iniciando SDK...');
    console.log('[FHVitals] 📊 Estado actual - initialized:', this.initialized, 'measuring:', this.measuring);
    
    // Si ya está inicializado, intentar usar la instancia existente
    if (this.initialized && this.sdk) {
      console.log('[FHVitals] ⚠️ SDK ya inicializado, reutilizando instancia existente');
      this.onResultCallback = callbacks.onResult;
      this.onEventCallback = callbacks.onEvent;
      return { error: "ERROR_NONE" };
    }

    // Verificar que el SDK esté cargado
    // NOTA: El SDK se exporta como window.FHVitals, NO como window.FHVitalsSDK
    if (typeof window.FHVitals === 'undefined') {
      console.log('[FHVitals] ⏳ SDK no encontrado, esperando 1s...');
      // Esperar un momento por si está cargando
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      if (typeof window.FHVitals === 'undefined') {
        console.error('[FHVitals] ❌ SDK no disponible después de espera');
        console.error('[FHVitals] 📊 window.FHVitals:', window.FHVitals);
        console.error('[FHVitals] 📊 window.FHVitalsSDK:', window.FHVitalsSDK);
        throw new Error('FHVitals SDK no está disponible. Asegúrate de que el script esté cargado en index.html');
      }
    }

    console.log('[FHVitals] ✅ SDK encontrado, versión:', window.FHVitals.version);
    
    this.sdk = window.FHVitals;
    this.onResultCallback = callbacks.onResult;
    this.onEventCallback = callbacks.onEvent;

    console.log('[FHVitals] 🔧 Configuración:', {
      camera_prepare_second: 5,
      assets_folder_path: ASSETS_PATH,
      auth_url: AUTH_URL.substring(0, 30) + '...'
    });

    try {
      const result = await this.sdk.init({
        on_result: this.handleResult.bind(this),
        on_event: this.handleEvent.bind(this),
        config: {
          camera_prepare_second: 5,
          assets_folder_path: ASSETS_PATH,
          auth_url: AUTH_URL
        }
      });

      console.log('[FHVitals] 📊 Resultado init:', result);

      // El SDK puede retornar error como string "ERROR_NONE" o número 0
      const isSuccess = result.error === 0 || result.error === "ERROR_NONE";
      
      if (isSuccess) {
        this.initialized = true;
        console.log('[FHVitals] ✅ SDK inicializado correctamente');
      } else if (result.error === "ERROR_HAS_INITIALIZED") {
        // SDK ya estaba inicializado globalmente, marcar como exitoso
        console.warn('[FHVitals] ⚠️ SDK ya estaba inicializado globalmente, continuando...');
        this.initialized = true;
        return { error: "ERROR_NONE" };
      } else {
        console.error('[FHVitals] ❌ Error en init, código:', result.error);
      }

      return result;
    } catch (error) {
      console.error('[FHVitals] ❌ Excepción durante init:', error);
      // Si el error es que ya está inicializado, no es crítico
      if (error.message && error.message.includes('initialized')) {
        console.warn('[FHVitals] ⚠️ SDK ya inicializado, marcando como exitoso');
        this.initialized = true;
        return { error: "ERROR_NONE" };
      }
      throw error;
    }
  }

  /**
   * DEPRECATED: Ya no es necesario cargar el script dinámicamente
   * El script se carga en index.html
   */
  loadSDKScript() {
    return Promise.resolve();
  }

  /**
   * Inicia el preview de la cámara
   * @param {string} canvasId - ID del elemento canvas
   * @param {string} facingMode - 'user' (frontal) o 'environment' (trasera)
   */
  async startPreview(canvasId, facingMode = 'user') {
    console.log('[FHVitals] 📹 Iniciando preview de cámara...', { canvasId, facingMode });
    
    if (!this.initialized) {
      console.error('[FHVitals] ❌ SDK no inicializado');
      throw new Error('SDK no inicializado');
    }
    
    const result = await this.sdk.startPreview(canvasId, facingMode);
    console.log('[FHVitals] 📊 Resultado startPreview:', result);
    
    const isSuccess = result.error === 0 || result.error === "ERROR_NONE";
    if (isSuccess) {
      console.log('[FHVitals] ✅ Preview iniciado correctamente');
    } else {
      console.error('[FHVitals] ❌ Error en preview, código:', result.error);
    }
    
    return result;
  }

  /**
   * Inicia la medición de signos vitales
   * @param {Object} userInfo - { age, sex, height, weight, bp_mode, bp_group }
   */
  async startMeasuring(userInfo) {
    console.log('[FHVitals] 🩺 Iniciando medición...', { userInfo });
    
    if (!this.initialized) {
      console.error('[FHVitals] ❌ SDK no inicializado');
      throw new Error('SDK no inicializado');
    }
    
    this.measuring = true;
    
    const measurementParams = {
      height: userInfo.height,
      weight: userInfo.weight,
      sex: userInfo.sex, // 0 = female, 1 = male
      age: userInfo.age,
      bp_mode: userInfo.bp_mode || "ternary",
      bp_group: userInfo.bp_group || "normal",
      facing_mode: "user",
      virtual_id: ""
    };

    console.log('[FHVitals] 📊 Parámetros de medición:', measurementParams);

    const result = await this.sdk.startMeasuring(measurementParams);
    console.log('[FHVitals] 📊 Resultado startMeasuring:', result);
    
    const isSuccess = result.error === 0 || result.error === "ERROR_NONE";
    if (isSuccess) {
      console.log('[FHVitals] ✅ Medición iniciada correctamente');
    } else {
      console.error('[FHVitals] ❌ Error al iniciar medición, código:', result.error);
    }
    
    return result;
  }

  /**
   * Detiene la medición
   */
  async stopMeasuring() {
    if (!this.initialized) {
      return;
    }
    this.measuring = false;
    return await this.sdk.stopMeasuring();
  }

  /**
   * Detiene el preview de la cámara
   */
  async stopPreview() {
    if (!this.initialized) {
      return;
    }
    return await this.sdk.stopPreview();
  }

  /**
   * Obtiene SBP/DBP calibrados contra valores de referencia (cuff BP).
   * Disponible solo tras procesar >270 frames (~9s @ 30fps); más confiable >512 frames.
   * @param {Number} refSbp - SBP de referencia (cuff)
   * @param {Number} refDbp - DBP de referencia (cuff)
   * @returns {Array<Number>|null} [sbp, dbp] o null si SDK no inicializado
   */
  getCalibratedBP(refSbp, refDbp) {
    if (!this.initialized || !this.sdk?.getCalibratedBP) return null;
    try {
      return this.sdk.getCalibratedBP(refSbp, refDbp);
    } catch (e) {
      console.warn('[FHVitals] getCalibratedBP error:', e);
      return null;
    }
  }

  /**
   * Riesgo (0-1) de que los valores de PA de referencia estén mal.
   * High: >0.6 · Medium: 0.35-0.6 · Low: <0.35
   * Requiere age, BMI y refBP previamente provistos.
   * @returns {Number|null}
   */
  getMiscalibrationRisk() {
    if (!this.initialized || !this.sdk?.getMiscalibrationRisk) return null;
    try {
      return this.sdk.getMiscalibrationRisk();
    } catch (e) {
      console.warn('[FHVitals] getMiscalibrationRisk error:', e);
      return null;
    }
  }

  /**
   * Handler interno para resultados
   */
  handleResult(result) {
    // Log compacto de progreso cada 10 frames
    if (result.frame_id % 10 === 0) {
      console.log('[FHVitals] 📈 Progreso:', {
        frame: result.frame_id,
        HR: result.HR,
        BP: `${result.BP_sys}/${result.BP_dia}`,
        SpO2: result.SpO2,
        status: result.scanning_status || 'OK'
      });
    }
    
    if (this.onResultCallback) {
      this.onResultCallback(result);
    }
  }

  /**
   * Handler interno para eventos
   */
  handleEvent(event) {
    console.log('[FHVitals] 📡 Evento:', event);
    
    // Logs especiales para eventos importantes
    if (event.state === '_camera_ready_') {
      console.log('[FHVitals] ✅ Cámara lista');
    } else if (event.state === '_connection_activate_') {
      console.log('[FHVitals] ✅ Conexión activada con servidor');
    } else if (event.state === '_connection_close_') {
      this.measuring = false;
      if (event.reason === 'normal') {
        console.log('[FHVitals] ℹ️ Conexión cerrada normalmente:', event.reason);
      } else {
        console.error('[FHVitals] ❌ Conexión cerrada:', event.reason);
      }
    } else if (event.state === '_restart_') {
      this.measuring = false;
      console.warn('[FHVitals] ⚠️ Reinicio solicitado:', event.reason);
    }
    
    if (this.onEventCallback) {
      this.onEventCallback(event);
    }
  }

  /**
   * Limpia recursos
   */
  async cleanup() {
    console.log('[FHVitals] 🧹 Iniciando cleanup...');
    
    if (this.measuring) {
      console.log('[FHVitals] 🛑 Deteniendo medición...');
      await this.stopMeasuring();
    }
    
    if (this.initialized) {
      console.log('[FHVitals] 📹 Deteniendo preview...');
      await this.stopPreview();
    }
    
    // Resetear estado para permitir reinicialización
    this.initialized = false;
    this.measuring = false;
    this.sdk = null;
    
    console.log('[FHVitals] ✅ Cleanup completado');
  }
}

/**
 * Mapea resultados del SDK a estructura de WE360
 * Incluye TODAS las métricas disponibles de FaceHeart v5.9.3
 * @param {Object} result - Resultado del SDK
 * @returns {Object} Datos mapeados para WE360
 */
export function mapSDKResultToWE360(result) {
  console.log('[fhvitals] 🔄 Mapeando resultado SDK v5.9.3:', result);

  const hrvIdx = result.hrv_indices || {};
  const imgQ = result.image_quality || {};
  const sigQ = result.signal_quality || {};
  const ans = result.ANSIndex || {};
  const cvd = result.cvd_risk || {};
  const hba1c = result.HbA1c || {};
  const chol = result.cholesterol || {};

  const mapped = {
    // Signos vitales principales
    heartRate: result.hr ?? null,
    heartRateVariability: result.hrv ?? null,
    bloodPressureSystolic: result.sbp ?? null,
    bloodPressureDiastolic: result.dbp ?? null,
    oxygenSaturation: result.spo2 ?? null,
    respirationRate: result.rr ?? null,
    stressIndex: result.si ?? null,

    // Métricas cardiovasculares adicionales
    cardiovascularAge: result.cardiovascular_age ?? null,
    cardiovascularBMI: result.cardiovascular_bmi ?? null,
    cvdRisk: cvd.percentage ?? null,
    cvdRiskPoints: cvd.points ?? null,
    wellnessScore: result.wellness_score ?? null,
    shockIndex: result.shock_index ?? null,

    // Índices autonómicos
    ansIndexPNS: ans.PNS ?? null,
    ansIndexSNS: ans.SNS ?? null,
    prq: result.PRQ ?? null,

    // HRV detallado (nuevo en 5.9.3)
    hrvHF: hrvIdx.HF ?? null,
    hrvLF: hrvIdx.LF ?? null,
    hrvLFHFRatio: hrvIdx.LF_HF_RATIO ?? null,
    hrvMeanRR: hrvIdx.MEAN_RR ?? null,
    hrvPHF: hrvIdx.PHF ?? null,
    hrvPLF: hrvIdx.PLF ?? null,
    hrvRMSSD: hrvIdx.RMSSD ?? null,
    hrvRRIV: hrvIdx.RRIV ?? null,
    hrvSDNN: hrvIdx.SDNN ?? null,
    hrvSD1: hrvIdx.SD1 ?? null,
    hrvSD2: hrvIdx.SD2 ?? null,
    hrvSDNNI: hrvIdx.SDNNI ?? null,
    hrvBufferReady: result.check_feature_buffer_for_hrv ?? null,

    // Health indices (0-5) — nuevo en 5.9.3
    healthIndexActivity: result.activity ?? null,
    healthIndexEquilibrium: result.equilibrium ?? null,
    healthIndexHealth: result.health ?? null,
    healthIndexMetabolism: result.metabolism ?? null,
    healthIndexRelaxation: result.relaxation ?? null,
    healthIndexSleep: result.sleep ?? null,

    // Biomarcadores estimados — nuevo en 5.9.3
    hba1cValue: hba1c.value ?? null,
    hba1cRisk: hba1c.risk ?? null,
    hba1cRange: hba1c.range ?? null,
    hemoglobin: result.hemoglobin ?? null,
    cholesterolValue: chol.value ?? null,
    cholesterolRisk: chol.risk ?? null,
    cholesterolRange: chol.range ?? null,

    // Detección arritmia (FAILED/NSR/AFIB) — nuevo en 5.9.3
    afib: result.afib ?? null,

    // Riesgo de mal-clasificación de grupo PA (0-1) — nuevo en 5.9.3
    // Usado para enviar pre-alertas al IHO (transcript 27/05/2026)
    wrongInputGroupRisk: result.wrong_input_group_risk ?? null,

    // Flags de rango de PA — nuevo en 5.9.3
    reachMaxSBP: result.reach_max_sbp ?? null,
    reachMaxDBP: result.reach_max_bdp ?? null,

    // Calidad de imagen — nuevo en 5.9.3
    imageBrightness: imgQ.brightness ?? null,
    imageContrast: imgQ.contrast ?? null,
    imageMotion: imgQ.motion ?? null,

    // Calidad de señal (objeto completo)
    signalQuality: sigQ,
    signalQualityHrHrv: sigQ.hr_hrv ?? null,
    signalQualityBP: sigQ.bp ?? null,
    signalQualityResp: sigQ.resp ?? null,
    signalQualitySpO2: sigQ.spo2 ?? null,

    // Metadata
    frameId: result.frame_id,
    scanningStatus: result.scanning_status || '',
    averageProcessTime: result.average_process_time ?? null,
    faceLossFrames: result.face_loss ?? null,

    // SDK version (auditoría)
    sdkVersion: '5.9.3',

    // Timestamp
    measurementDate: new Date().toISOString(),
    measurementMethod: 'camera'
  };

  console.log('[fhvitals] ✅ Resultado mapeado:', mapped);
  return mapped;
}

/**
 * Calcula el grupo de presión arterial según valores actuales
 * @param {number} systolic - PA sistólica
 * @param {number} diastolic - PA diastólica
 * @returns {string} 'normal' | 'prehypertension' | 'hypertension'
 */
export function calculateBPGroup(systolic, diastolic) {
  if (!systolic || !diastolic) return 'normal';
  
  if (systolic >= 140 || diastolic >= 90) {
    return 'hypertension';
  } else if (systolic >= 120 || diastolic >= 80) {
    return 'prehypertension';
  } else {
    return 'normal';
  }
}
